<template>
  <div class="content d-flex flex-column flex-column-fluid mb-10" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack">
        <div class="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
          <h1 class="d-flex text-dark fw-bolder fs-3 align-items-center my-1">{{ $t('navigation.my_deliveries') }}</h1>
          <span class="h-20px border-gray-300 border-start mx-4 d-none d-md-block"></span>
          <ul class="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-1 d-none d-md-flex">
            <li class="breadcrumb-item text-muted">
              <router-link to="/admin/dashboard" class="text-muted text-hover-primary">{{ $t('breadcrumbs.store.dashboard') }}</router-link>
              <span class="bullet bg-gray-200 w-5px h-2px m-2"></span>
            </li>
            <li class="breadcrumb-item text-dark">{{ $t('navigation.my_deliveries') }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-xxl">
        <div class="card card-flush">
          <div class="card-header border-0 pt-6">
            <div class="card-title">
              <div class="d-flex align-items-center">
                <div class="card-icon-delivery bg-light-primary me-3">
                  <i class="bi bi-truck text-primary fs-2"></i>
                </div>
                <div>
                  <h3 class="fw-bold mb-0 fs-4">{{ $t('navigation.my_deliveries') }}</h3>
                </div>
              </div>
            </div>
            <div class="card-toolbar">
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <button class="btn btn-sm" :class="statusFilter === '' ? 'btn-primary' : 'btn-light'" @click="filterByStatus('')">
                  {{ $t('delivery.all') }}
                </button>
                <button class="btn btn-sm" :class="statusFilter === 'PENDING' ? 'btn-warning' : 'btn-light'" @click="filterByStatus('PENDING')">
                  {{ $t('delivery.pending') }}
                </button>
                <button class="btn btn-sm" :class="statusFilter === 'DELIVERED' ? 'btn-success' : 'btn-light'" @click="filterByStatus('DELIVERED')">
                  {{ $t('delivery.delivered') }}
                </button>
                <!-- actualiser : nouvelles livraisons assignées depuis l'ouverture de la page -->
                <button class="btn btn-sm btn-icon btn-light-primary" :title="$t('delivery.refresh')" :disabled="isLoading" @click="loadDeliveries">
                  <i class="bi bi-arrow-clockwise fs-4" :class="{ 'spin': isLoading }"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="card-body pt-0">
            <div v-if="isLoading" class="text-center py-10">
              <span class="spinner-border spinner-border-sm align-middle me-2"></span>
              {{ $t('loading') }}
            </div>

            <div v-else-if="deliveries.length === 0" class="text-center text-muted py-10">
              <i class="bi bi-inbox fs-2x d-block mb-3"></i>
              {{ $t('delivery.no_deliveries') }}
            </div>

            <template v-else>
              <!-- Desktop -->
              <div class="d-none d-md-block">
                <div class="table-responsive">
                  <table class="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                    <thead>
                      <tr class="fw-bold text-muted bg-light">
                        <th class="ps-4 rounded-start">{{ $t('delivery.order_number') }}</th>
                        <th>{{ $t('delivery.customer') }}</th>
                        <th>{{ $t('delivery.address') }}</th>
                        <th>{{ $t('delivery.shipping_date') }}</th>
                        <th class="text-center">{{ $t('delivery.status') }}</th>
                        <th class="pe-4 text-end rounded-end">{{ $t('delivery.actions') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="delivery in deliveries" :key="delivery.id">
                        <td class="ps-4">
                          <span class="fw-bold text-gray-800">#{{ delivery.checkout_number }}</span>
                        </td>
                        <td>
                          <span class="text-gray-800">{{ delivery.customer_name }}</span>
                          <div v-if="delivery.amount_to_collect > 0" class="fs-8 fw-bold text-warning">{{ $t('delivery.to_collect') }} : {{ money(delivery.amount_to_collect, delivery) }}</div>
                          <div v-else class="fs-8 text-success">{{ $t('delivery.already_paid') }}</div>
                          <div v-if="delivery.customer_note" class="fs-8 text-gray-700 text-truncate" style="max-width: 220px" :title="delivery.customer_note">💬 {{ delivery.customer_note }}</div>
                        </td>
                        <td>
                          <span v-if="delivery.shipping_address" class="text-gray-700">
                            <div>{{ [delivery.shipping_address.quartier, delivery.shipping_address.city].filter(Boolean).join(', ') || delivery.shipping_address.location_label || '—' }}</div>
                            <div v-if="delivery.shipping_address.point_de_repere" class="text-muted fs-8">
                              <i class="bi bi-signpost-2 me-1"></i>{{ delivery.shipping_address.point_de_repere }}
                            </div>
                            <a
                              v-if="delivery.shipping_address.latitude && delivery.shipping_address.longitude"
                              :href="`https://www.google.com/maps?q=${delivery.shipping_address.latitude},${delivery.shipping_address.longitude}`"
                              target="_blank"
                              class="btn btn-xs btn-light-primary mt-1 px-2 py-1 fs-8"
                            >
                              <i class="bi bi-map me-1"></i>{{ $t('destockage.view_on_map') }}
                            </a>
                          </span>
                          <span v-else class="text-muted">—</span>
                        </td>
                        <td>
                          <span class="text-gray-600">{{ formatDate(delivery.delivery_date) }}</span>
                        </td>
                        <td class="text-center">
                          <span class="badge fs-7 px-3 py-2" :class="`badge-light-${delivery.status_badge || 'secondary'}`">
                            {{ delivery.status_label }}
                          </span>
                        </td>
                        <td class="pe-4 text-end">
                          <button
                            class="btn btn-icon btn-light-primary btn-sm me-1"
                            :title="$t('delivery.view_details')"
                            @click="openDetail(delivery)"
                          >
                            <i class="bi bi-eye"></i>
                          </button>
                          <button
                            v-if="delivery.status === 'PENDING'"
                            class="btn btn-icon btn-light-success btn-sm"
                            :title="$t('delivery.mark_delivered')"
                            @click="confirmMarkAsDelivered(delivery)"
                          >
                            <i class="bi bi-check2-circle"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Mobile: Cards -->
              <div class="d-md-none">
                <div class="d-flex flex-column gap-4">
                  <div v-for="delivery in deliveries" :key="delivery.id"
                       class="delivery-card border border-gray-300 rounded p-4">
                    <div class="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <span class="fw-bold text-gray-800 fs-6">#{{ delivery.checkout_number }}</span>
                        <span class="btn btn-sm ms-2" :class="`btn-light-${delivery.status_badge || 'secondary'}`">
                          {{ delivery.status_label || '-' }}
                        </span>
                      </div>
                      <div class="d-flex gap-1">
                        <button class="btn btn-icon btn-light-primary btn-sm" @click="openDetail(delivery)">
                          <i class="bi bi-eye"></i>
                        </button>
                        <button
                          v-if="delivery.status === 'PENDING'"
                          class="btn btn-icon btn-light-success btn-sm"
                          @click="confirmMarkAsDelivered(delivery)"
                        >
                          <i class="bi bi-check2-circle"></i>
                        </button>
                      </div>
                    </div>
                    <div class="d-flex flex-column gap-1">
                      <div class="d-flex align-items-center text-gray-700">
                        <i class="bi bi-person me-2 text-muted"></i>
                        <span>{{ delivery.customer_name }}</span>
                      </div>
                      <div v-if="delivery.shipping_address" class="d-flex align-items-start text-gray-700">
                        <i class="bi bi-geo-alt me-2 text-muted mt-1"></i>
                        <div>
                          <div>{{ [delivery.shipping_address.quartier, delivery.shipping_address.city].filter(Boolean).join(', ') || delivery.shipping_address.location_label || '—' }}</div>
                          <div v-if="delivery.shipping_address.point_de_repere" class="text-muted fs-8">
                            <i class="bi bi-signpost-2 me-1"></i>{{ delivery.shipping_address.point_de_repere }}
                          </div>
                          <a
                            v-if="delivery.shipping_address.latitude && delivery.shipping_address.longitude"
                            :href="`https://www.google.com/maps?q=${delivery.shipping_address.latitude},${delivery.shipping_address.longitude}`"
                            target="_blank"
                            class="btn btn-xs btn-light-primary mt-1 px-2 py-1 fs-8"
                          >
                            <i class="bi bi-map me-1"></i>{{ $t('destockage.view_on_map') }}
                          </a>
                        </div>
                      </div>
                      <div class="d-flex align-items-center text-gray-600">
                        <i class="bi bi-calendar3 me-2 text-muted"></i>
                        <span class="fs-7">{{ formatDate(delivery.delivery_date) }}</span>
                      </div>
                      <div class="d-flex align-items-center">
                        <i class="bi bi-cash-coin me-2 text-muted"></i>
                        <span v-if="delivery.amount_to_collect > 0" class="fw-bold text-warning">{{ $t('delivery.to_collect') }} : {{ money(delivery.amount_to_collect, delivery) }}</span>
                        <span v-else class="text-success fs-7">{{ $t('delivery.already_paid') }}</span>
                      </div>
                      <div v-if="delivery.customer_note" class="d-flex align-items-start text-gray-700 fs-7">
                        <i class="bi bi-chat-left-text me-2 text-warning"></i><span class="text-truncate">{{ delivery.customer_note }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- ─── Modal Détail Livraison ──────────────────────────────── -->
    <div v-if="selectedDelivery" class="modal-overlay" @click.self="closeDetail">
      <div class="modal-detail">

        <!-- Header -->
        <div class="modal-detail-header">
          <div>
            <span class="fw-bold fs-5 text-gray-800">#{{ selectedDelivery.checkout_number }}</span>
            <span class="badge ms-2" :class="`badge-light-${selectedDelivery.status_badge || 'secondary'}`">
              {{ selectedDelivery.status_label }}
            </span>
          </div>
          <button class="btn btn-icon btn-sm btn-light" @click="closeDetail">
            <i class="bi bi-x fs-4"></i>
          </button>
        </div>

        <div class="modal-detail-body">

          <!-- Paiement : montant à encaisser (paiement à la livraison) ou déjà payé -->
          <div class="collect-box" :class="selectedDelivery.amount_to_collect > 0 ? 'collect-box--due' : 'collect-box--paid'">
            <i class="bi" :class="selectedDelivery.amount_to_collect > 0 ? 'bi-cash-coin' : 'bi-check-circle-fill'"></i>
            <div class="flex-grow-1">
              <div class="fw-bold">{{ selectedDelivery.amount_to_collect > 0 ? $t('delivery.to_collect') : $t('delivery.already_paid') }}</div>
              <div class="fs-8 opacity-75">{{ $t('delivery.order_total') }} : {{ money(selectedDelivery.amount_ttc, selectedDelivery) }}</div>
            </div>
            <strong v-if="selectedDelivery.amount_to_collect > 0" class="fs-3">{{ money(selectedDelivery.amount_to_collect, selectedDelivery) }}</strong>
          </div>

          <!-- Précision laissée par le client à la commande -->
          <div v-if="selectedDelivery.customer_note" class="alert alert-warning py-3 px-4 mb-4">
            <div class="fw-bold mb-1"><i class="bi bi-chat-left-text me-2"></i>{{ $t('customer_note.title') }}</div>
            <div style="white-space: pre-line">{{ selectedDelivery.customer_note }}</div>
          </div>

          <!-- Client -->
          <div class="detail-section">
            <div class="detail-section-title">
              <i class="bi bi-person-fill text-primary me-2"></i>{{ $t('delivery.customer') }}
            </div>
            <div class="detail-value fw-bold fs-6">{{ selectedDelivery.customer_name }}</div>
          </div>

          <!-- Adresse de livraison -->
          <div v-if="selectedDelivery.shipping_address" class="detail-section">
            <div class="detail-section-title">
              <i class="bi bi-geo-alt-fill text-danger me-2"></i>{{ $t('delivery.shipping_address') }}
            </div>
            <div class="detail-value">
              <div class="fw-bold">
                {{ [selectedDelivery.shipping_address.quartier, selectedDelivery.shipping_address.city, selectedDelivery.shipping_address.country].filter(Boolean).join(', ') || selectedDelivery.shipping_address.location_label || '—' }}
              </div>
              <div v-if="isValidAddress(selectedDelivery.shipping_address.address)" class="text-muted fs-7 mt-1">
                <i class="bi bi-house me-1"></i>{{ selectedDelivery.shipping_address.address }}
              </div>
              <div v-if="selectedDelivery.shipping_address.point_de_repere" class="text-muted mt-1">
                <i class="bi bi-signpost-2 me-1"></i>
                <em>{{ selectedDelivery.shipping_address.point_de_repere }}</em>
              </div>
              <a
                v-if="selectedDelivery.shipping_address.latitude && selectedDelivery.shipping_address.longitude"
                :href="`https://www.google.com/maps?q=${selectedDelivery.shipping_address.latitude},${selectedDelivery.shipping_address.longitude}`"
                target="_blank"
                class="btn btn-sm btn-light-primary mt-2"
              >
                <i class="bi bi-map me-1"></i>{{ $t('destockage.view_on_map') }}
              </a>
            </div>
          </div>

          <!-- Téléphone -->
          <div v-if="selectedDelivery.shipping_address?.phone" class="detail-section">
            <div class="detail-section-title">
              <i class="bi bi-telephone-fill text-success me-2"></i>{{ $t('delivery.phone') }}
            </div>
            <div class="detail-value d-flex align-items-center gap-2">
              <a :href="`tel:${selectedDelivery.shipping_address.phone?.startsWith('+') ? selectedDelivery.shipping_address.phone : (selectedDelivery.shipping_address.phone_code + selectedDelivery.shipping_address.phone)}`"
                 class="fw-bold text-gray-800 text-hover-primary">
                {{ selectedDelivery.shipping_address.phone?.startsWith('+') ? selectedDelivery.shipping_address.phone : (selectedDelivery.shipping_address.phone_code + ' ' + selectedDelivery.shipping_address.phone) }}
              </a>
              <!-- Numéro WhatsApp : ouvre la conversation au clic -->
              <a v-if="selectedDelivery.shipping_address.is_whatsapp && whatsappUrl(selectedDelivery.shipping_address.phone, selectedDelivery.shipping_address.phone_code)"
                 :href="whatsappUrl(selectedDelivery.shipping_address.phone, selectedDelivery.shipping_address.phone_code)"
                 target="_blank" rel="noopener" class="badge badge-light-success text-hover-success">
                <i class="bi bi-whatsapp me-1"></i>WhatsApp
              </a>
            </div>
          </div>

          <!-- Produits commandés -->
          <div class="detail-section">
            <div class="detail-section-title">
              <i class="bi bi-bag-fill text-primary me-2"></i>{{ $t('delivery.products') }}
            </div>
            <div class="detail-value">
              <div v-if="isLoadingNote" class="text-muted fs-7">
                <span class="spinner-border spinner-border-sm me-1"></span>{{ $t('loading') }}
              </div>
              <template v-else-if="deliveryNote?.items?.length">
                <div v-for="item in deliveryNote.items" :key="item.name" class="d-flex justify-content-between align-items-center py-2 border-bottom border-dashed">
                  <span class="text-gray-700">{{ item.name }}</span>
                  <span class="fw-bold text-gray-800 ms-3" style="margin-right: 10px">× {{ item.quantity }}</span>
                </div>
              </template>
              <div v-else-if="!isLoadingNote" class="text-muted fs-7">—</div>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div v-if="selectedDelivery.status === 'PENDING'" class="modal-detail-footer">
          <button
            class="btn btn-success btn-sm w-100"
            @click="confirmMarkAsDelivered(selectedDelivery)"
          >
            <i class="bi bi-check2-circle me-1"></i>{{ $t('delivery.mark_delivered') }}
          </button>
        </div>

      </div>
    </div>

    <!-- code de remise du client, demandé pour marquer livrée -->
    <HandoverCodeModal
      v-model:show="showHandover"
      :checkout-id="handoverDelivery?.checkout_id"
      :amount-to-collect="handoverDelivery?.amount_to_collect > 0 ? money(handoverDelivery.amount_to_collect, handoverDelivery) : ''"
      :submit="submitHandover"
    />

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import HandoverCodeModal from '@/components/modals/delivery/HandoverCodeModal.vue'
import { formattedLocalAmount } from '@/mixins/Helper.vue'
import { whatsappUrl } from '@/helpers/whatsapp'
import { useStore } from 'vuex'
import i18n from '@/plugins/i18n'

const store = useStore()

const deliveries      = ref([])
const isLoading       = ref(false)
const statusFilter    = ref('')
const selectedDelivery = ref(null)
const deliveryNote    = ref(null)
const isLoadingNote   = ref(false)

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isValidAddress(str) {
  return !!str && !UUID_RE.test(str.trim())
}

function formatDate(dateString) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

async function loadDeliveries() {
  isLoading.value = true
  try {
    const params = {}
    if (statusFilter.value) params.status = statusFilter.value
    const response = await store.dispatch('ecommerce/getMyDeliveries', params)
    deliveries.value = response?.data || []
  } catch (e) {
    console.error('Erreur chargement livraisons:', e)
  } finally {
    isLoading.value = false
  }
}

function filterByStatus(status) {
  statusFilter.value = status
  loadDeliveries()
}

async function openDetail(delivery) {
  selectedDelivery.value = delivery
  deliveryNote.value     = null
  isLoadingNote.value    = true
  try {
    const res = await store.dispatch('ecommerce/getCheckoutDeliveryNote', { checkout_id: delivery.checkout_id })
    deliveryNote.value = res?.data || null
  } catch {
    deliveryNote.value = null
  } finally {
    isLoadingNote.value = false
  }
}

function closeDetail() {
  selectedDelivery.value = null
  deliveryNote.value     = null
}

// « 5 495 FCFA »
const money = (amount, delivery) => `${formattedLocalAmount(amount)} ${delivery?.currency_symbol || ''}`.trim()

const showHandover = ref(false)
const handoverDelivery = ref(null)

function submitHandover(handover) {
  return store.dispatch('ecommerce/markOrderAsDelivered', { checkout_id: handoverDelivery.value.checkout_id, ...handover, silent: true }).then(() => {
    window.toastr['success'](i18n.global.t('delivery.marked_delivered'))
    closeDetail()
    loadDeliveries()
  })
}

function confirmMarkAsDelivered(delivery) {
  // commande avec code de remise : le livreur le demande au client
  if (delivery.requires_handover_code) {
    handoverDelivery.value = delivery
    showHandover.value = true
    return
  }
  Swal.fire({
    title: i18n.global.t('delivery.swal_title'),
    text: i18n.global.t('delivery.swal_text').replace('{number}', delivery.checkout_number),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: i18n.global.t('swal.yes'),
    cancelButtonText: i18n.global.t('swal.no'),
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await store.dispatch('ecommerce/markOrderAsDelivered', delivery.checkout_id)
        window.toastr['success'](i18n.global.t('delivery.marked_delivered'))
        closeDetail()
        loadDeliveries()
      } catch (e) {
        window.toastr['error'](e?.response?.data?.message || 'Erreur lors de la mise à jour')
      }
    }
  })
}

onMounted(() => {
  loadDeliveries()
})
</script>

<style scoped>
.spin { display: inline-block; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.collect-box {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
  padding: 14px 16px;
  border-radius: 10px;
}
.collect-box > i { font-size: 1.6rem; }
.collect-box--due { background: #fff8dd; color: #7e5a00; }
.collect-box--due > i { color: #f6a609; }
.collect-box--paid { background: #e8fff3; color: #1e7e4a; }
.badge-light-success   { background-color: rgba(76, 175, 80, 0.1);  color: #4CAF50; }
.badge-light-warning   { background-color: rgba(255, 152, 0, 0.1);  color: #FF9800; }
.badge-light-secondary { background-color: rgba(180, 180, 180, 0.15); color: #7e8299; }

.card-icon-delivery {
  width: 50px; height: 50px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
}

.delivery-card { transition: all 0.2s ease; }
.delivery-card:hover { background-color: #f8f9fa; border-color: #3699FF !important; }

/* ── Modal Overlay ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-detail {
  background: #fff;
  width: 100%;
  max-width: 480px;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.18);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.97); }
  to   { opacity: 1; transform: scale(1); }
}

.modal-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.25rem 1rem;
  border-bottom: 1px solid #f1f1f1;
}

.modal-detail-body {
  flex: 1;
  overflow-y: auto;
  padding: 0.25rem 1.5rem 0.5rem;
  -webkit-overflow-scrolling: touch;
}

.detail-section {
  padding: 0.85rem 0;
  border-bottom: 1px solid #f5f5f5;
}
.detail-section:last-child { border-bottom: none; }

.detail-section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #a1a5b7;
  margin-bottom: 0.4rem;
}

.detail-value { font-size: 15px; color: #181c32; }

.modal-detail-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.9rem 1.25rem;
  border-top: 1px solid #f1f1f1;
  gap: 0.75rem;
}

@media (max-width: 767.98px) {
  .card-header { flex-direction: column; align-items: flex-start !important; gap: 1rem; }
  .card-toolbar { width: 100%; }
  .card-toolbar .d-flex { width: 100%; justify-content: space-between; }
}
</style>
